import { Link } from "react-router-dom";
import type { Topic } from "@/mocks/topics";

interface TopicCardProps {
  topic: Topic;
  featured?: boolean;
  compact?: boolean;
}

export default function TopicCard({ topic, featured = false, compact = false }: TopicCardProps) {
  if (compact) {
    return (
      <Link
        to={`/topic/${topic.id}`}
        className="group flex gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 hover:bg-background-100"
      >
        <div className="h-20 w-28 flex-none overflow-hidden rounded-md bg-background-200">
          <img
            src={topic.cover}
            alt={topic.title}
            title={`${topic.title} - 网红大瓜`}
            className="h-full w-full object-top"
          />
        </div>
        <div className="min-w-0">
          <span className="font-label text-xs text-primary-700">{topic.category}</span>
          <h3 className="mt-1 line-clamp-2 font-heading text-sm font-semibold text-foreground-950 group-hover:text-primary-700">
            {topic.title}
          </h3>
          <div className="mt-2 flex items-center gap-3 font-label text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-fire-line"></i>
              {topic.heat}
            </span>
            <span>{topic.date}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <article
      className={`group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300 ${
        featured ? "lg:flex-row" : ""
      }`}
    >
      <Link
        to={`/topic/${topic.id}`}
        className={`block flex-none overflow-hidden bg-background-200 ${
          featured ? "lg:w-1/2" : ""
        }`}
      >
        <div className={featured ? "aspect-[16/10] lg:h-full lg:aspect-auto" : "aspect-[16/10]"}>
          <img
            src={topic.cover}
            alt={topic.title}
            title={`${topic.title} - 网红大瓜`}
            className="h-full w-full object-top transition-transform duration-500 group-hover:scale-[1.03]"
          />
        </div>
      </Link>

      <div className="flex flex-1 flex-col p-5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-primary-100 px-3 py-1 font-label text-xs font-medium text-primary-800">
            {topic.category}
          </span>
          <span
            className={`rounded-full px-3 py-1 font-label text-xs ${
              topic.status === "持续发酵" || topic.status === "检测中"
                ? "bg-accent-100 text-accent-900"
                : "bg-secondary-100 text-secondary-900"
            }`}
          >
            {topic.status}
          </span>
        </div>

        <h3 className={`mt-3 font-heading font-bold leading-snug text-foreground-950 ${featured ? "text-xl md:text-2xl" : "text-lg"}`}>
          <Link to={`/topic/${topic.id}`} className="transition-colors hover:text-primary-700">
            {topic.title}
          </Link>
        </h3>

        <p className={`mt-3 font-label leading-relaxed text-foreground-600 ${featured ? "text-sm md:text-base" : "line-clamp-2 text-sm"}`}>
          {topic.summary}
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 font-label text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-fire-line text-primary-500"></i>
            热度 {topic.heat}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-links-line"></i>
            {topic.sourceCount} 方说法
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-time-line"></i>
            {topic.readingMinutes} 分钟
          </span>
        </div>

        <div className="mt-auto flex items-center justify-between pt-5">
          <span className="font-label text-xs text-foreground-500">{topic.date}</span>
          <Link
            to={`/topic/${topic.id}`}
            className="flex items-center gap-1 whitespace-nowrap rounded-md bg-background-100 px-3 py-1.5 font-label text-xs font-medium text-foreground-800 transition-colors hover:bg-primary-500 hover:text-background-50"
          >
            看多方说法
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </article>
  );
}